import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dumbbell, FileText, Tag, LogOut, Home, Menu, X, BarChart3, LayoutList, Inbox, TrendingUp } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/lib/api';

const NAV = [
  { to: '/admin', label: 'Tableau de bord', icon: Home, exact: true },
  { to: '/admin/demandes', label: 'Demandes', icon: Inbox, badge: 'new_count' },
  { to: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/admin/seo', label: 'SEO Auto-Pilote', icon: TrendingUp },
  { to: '/admin/blog', label: 'Blog', icon: FileText },
  { to: '/admin/pricing', label: 'Tarifs', icon: Tag },
  { to: '/admin/content', label: 'Contenu', icon: LayoutList },
];

export const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [newCount, setNewCount] = useState(0);

  // Lightweight badge poll for unread requests (admin pages only)
  useEffect(() => {
    let cancel = false;
    const fetchCount = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/contact/list`, { params: { only_new: 'true', limit: 200 } });
        if (!cancel) setNewCount((data || []).filter((c) => (c.handled_status || 'new') === 'new').length);
      } catch { /* ignore */ }
    };
    fetchCount();
    const i = setInterval(fetchCount, 60_000);
    return () => { cancel = true; clearInterval(i); };
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const isActive = (to, exact) => (exact ? location.pathname === to : location.pathname.startsWith(to));

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile topbar */}
      <div className="lg:hidden bg-dark text-white px-4 py-3 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-2">
          <Dumbbell className="h-5 w-5 text-primary-glow" />
          <span className="font-heading tracking-wider text-sm">ADMIN</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2" data-testid="admin-mobile-toggle">
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${sidebarOpen ? 'block' : 'hidden'} lg:block fixed lg:sticky top-0 lg:top-0 left-0 z-30 w-64 min-h-screen bg-dark text-white flex-shrink-0 flex flex-col`}
          data-testid="admin-sidebar"
        >
          <div className="p-6 border-b border-white/10 hidden lg:flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/20 border border-primary-glow/40 flex items-center justify-center">
              <Dumbbell className="h-4 w-4 text-primary-glow" />
            </div>
            <div>
              <div className="font-heading tracking-wider text-sm">ADMIN</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">CrossFit Saint-Chamond</div>
            </div>
          </div>

          <nav className="flex-1 py-6 px-3 space-y-1">
            {NAV.map(({ to, label, icon: Icon, exact, badge }) => {
              const active = isActive(to, exact);
              const showBadge = badge === 'new_count' && newCount > 0;
              return (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    active
                      ? 'bg-primary text-white shadow-[0_10px_30px_-10px_hsla(155,75%,18%,0.6)]'
                      : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}
                  data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="flex-1">{label}</span>
                  {showBadge && (
                    <span
                      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-dark text-[10px] font-bold tabular-nums"
                      data-testid="admin-nav-badge-demandes"
                    >
                      {newCount > 99 ? '99+' : newCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-white/10">
            <div className="mb-3 px-2">
              <div className="text-[10px] text-white/40 uppercase tracking-widest">Connecté en tant que</div>
              <div className="text-xs text-white/80 font-medium truncate" data-testid="admin-user-email">
                {user?.email}
              </div>
            </div>
            <Link to="/" className="block mb-2">
              <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10 bg-transparent text-xs" size="sm">
                Voir le site public
              </Button>
            </Link>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full text-white/70 hover:bg-white/10 hover:text-white text-xs"
              size="sm"
              data-testid="admin-logout"
            >
              <LogOut className="h-3.5 w-3.5 mr-2" />
              Déconnexion
            </Button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 p-4 md:p-8 lg:p-10" data-testid="admin-main">
          {children}
        </main>
      </div>
    </div>
  );
};
